/**
 * Feedback API client for submitting user feedback
 * Follows established patterns from conversations.ts and chat.ts
 */

import { apiClient } from './base/client';
import { ApiError } from './base/errors';

/**
 * Feedback request interface matching backend V3_SEND_FEEDBACK_DATA schema
 */
export interface FeedbackRequest {
  // Required fields
  responseID: string;
  feedbackType: 'positive' | 'negative';
  sharePrompt: 'yes' | 'no';
  feedbackSubmissionTime: string;
  appVersion: string;
  conversationID: string;

  // Optional fields
  comment?: string;
  issue?: string;
  userName?: string;
  query?: string;
  answer?: string;
  genie_space_name?: string;
  genie_conversation_id?: string;
  genie_message_id?: string;
}

/**
 * Feedback response interface matching backend response
 */
export interface FeedbackResponse {
  message: string;
}

/**
 * Custom error class for feedback-specific errors
 */
export class FeedbackError extends ApiError {
  public readonly name: string = 'FeedbackError';

  constructor(message: string, status?: number, code?: string, originalError?: ApiError) {
    super(message, status || originalError?.status || 400, code || 'FEEDBACK_ERROR');
    Object.setPrototypeOf(this, FeedbackError.prototype);
  }
}

/**
 * Submit user feedback to the backend
 */
export async function submitFeedback(request: FeedbackRequest): Promise<FeedbackResponse> {
  try {
    // Validate required fields
    if (!request.responseID) {
      throw new FeedbackError(
        'Response ID is required',
        400,
        'MISSING_RESPONSE_ID'
      );
    }

    if (!request.feedbackType) {
      throw new FeedbackError(
        'Feedback type is required',
        400,
        'MISSING_FEEDBACK_TYPE'
      );
    }

    if (!request.conversationID) {
      throw new FeedbackError(
        'Conversation ID is required',
        400,
        'MISSING_CONVERSATION_ID'
      );
    }

    // Make API request
    const response = await apiClient.post<FeedbackResponse>('/submit_feedback', request);

    // Validate response
    if (!response.message) {
      throw new FeedbackError(
        'Invalid response: missing confirmation message',
        undefined,
        'INVALID_RESPONSE'
      );
    }

    return response;
  } catch (error) {
    if (error instanceof FeedbackError) {
      throw error;
    }

    if (error instanceof ApiError) {
      throw new FeedbackError(
        `Failed to submit feedback: ${error.message}`,
        error.status,
        error.code,
        error
      );
    }

    throw new FeedbackError(
      `Failed to submit feedback: ${error instanceof Error ? error.message : 'Unknown error'}`,
      undefined,
      'SUBMIT_FAILED'
    );
  }
}

/**
 * Query key factory for feedback operations
 * Provides consistent cache keys for TanStack Query
 */
export const feedbackQueryKeys = {
  all: ['feedback'] as const,
  submissions: () => [...feedbackQueryKeys.all, 'submission'] as const,
  submit: (messageId: string) => [...feedbackQueryKeys.submissions(), messageId] as const,
};

/**
 * TanStack Query mutation options for feedback submission
 * Provides reusable, type-safe mutation configuration
 */
export const feedbackMutationOptions = (messageId: string) => ({
  mutationKey: feedbackQueryKeys.submit(messageId),
  mutationFn: (request: FeedbackRequest) => submitFeedback(request),

  // Retry configuration
  retry: (failureCount: number, error: any) => {
    // Don't retry authentication errors
    if (error instanceof ApiError && error.status === 401) {
      return false;
    }

    // Don't retry validation errors (4xx)
    if (error instanceof ApiError && error.status && error.status >= 400 && error.status < 500) {
      return false;
    }

    // Retry network errors and server errors up to 3 times
    return failureCount < 3;
  },

  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff

  // Mutation metadata
  meta: {
    actionType: 'submit-feedback',
    messageId,
  },
});

/**
 * Utility functions for feedback operations
 */
export const feedbackUtils = {
  /**
   * Validate feedback request data
   */
  validateFeedbackRequest: (request: Partial<FeedbackRequest>): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!request.responseID?.trim()) {
      errors.push('Response ID is required');
    }

    if (!request.feedbackType) {
      errors.push('Feedback type is required');
    }

    if (request.feedbackType && !['positive', 'negative'].includes(request.feedbackType)) {
      errors.push('Feedback type must be positive or negative');
    }

    if (!request.conversationID?.trim()) {
      errors.push('Conversation ID is required');
    }

    if (!request.feedbackSubmissionTime) {
      errors.push('Submission time is required');
    }

    if (!request.appVersion?.trim()) {
      errors.push('App version is required');
    }

    if (!['yes', 'no'].includes(request.sharePrompt || '')) {
      errors.push('Share prompt must be yes or no');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * Generate feedback submission timestamp
   */
  generateSubmissionTime: (): string => {
    return new Date().toISOString();
  },

  /**
   * Get app version from environment
   */
  getAppVersion: (): string => {
    return import.meta.env.VITE_APP_VERSION || '1.0.0';
  },

  /**
   * Create minimal feedback request
   */
  createBaseFeedbackRequest: (
    responseId: string,
    feedbackType: 'positive' | 'negative',
    conversationId: string,
    shareData: boolean
  ): Pick<FeedbackRequest, 'responseID' | 'feedbackType' | 'sharePrompt' | 'feedbackSubmissionTime' | 'appVersion' | 'conversationID'> => {
    return {
      responseID: responseId,
      feedbackType,
      sharePrompt: shareData ? 'yes' : 'no',
      feedbackSubmissionTime: feedbackUtils.generateSubmissionTime(),
      appVersion: feedbackUtils.getAppVersion(),
      conversationID: conversationId,
    };
  },
};
